/**
 * useReleaseClips Hook
 *
 * @module features/releases/hooks/useReleaseClips
 * @description Fetches auto-generated release clips and screenshots for a release QA run.
 * Polls while clips are expected but not yet fully available.
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { throwReleaseApiError } from '../releaseApiError';

export type ReleaseClipMetadata = {
  outputType: 'clip' | 'screenshot';
  durationMs?: number;
  centerMs: number;
  rationale: string;
  generatedAt: string;
};

export type ReleaseClip = {
  workflowRunId: string;
  workflowName: string | null;
  url: string;
  metadata: ReleaseClipMetadata;
};

type ReleaseClipsResponse = {
  ok: boolean;
  clips: ReleaseClip[];
  /** Number of regressions eligible for clip generation */
  expectedCount: number;
};

type ReleaseClipsResult = {
  clips: ReleaseClip[];
  expectedCount: number;
};

/**
 * Fetch release clips for a release QA run.
 * Only returns clips for passed regressions that have completed clip generation.
 *
 * When `expectMore` is true (run still active or report recently generated),
 * polls every 10 seconds until all expected clips have arrived.
 */
export function useReleaseClips(releaseRunId: string | undefined, expectMore = false) {
  const query = useQuery({
    queryKey: ['release-qa-clips', releaseRunId],
    queryFn: async (): Promise<ReleaseClipsResult> => {
      if (!releaseRunId) {
        throw new Error('Missing release run ID');
      }

      const response = await fetch(`${API_URL}/release-qa/runs/${releaseRunId}/clips`, {
        credentials: 'include',
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch release clips');
      }

      const data: ReleaseClipsResponse = await response.json();
      return { clips: data.clips, expectedCount: data.expectedCount };
    },
    enabled: !!releaseRunId,
    refetchInterval: (q) => {
      const result = q.state.data;
      if (!expectMore || !result) return expectMore ? 10_000 : false;

      // Keep polling until all expected clips have been generated
      if (result.clips.length < result.expectedCount) {
        return 10_000;
      }
      return false;
    },
  });

  return {
    ...query,
    // Expose clips directly for backward-compatible consumption
    data: query.data?.clips,
  };
}
